// Layer: presentation. The fallback playbooks, mirroring storia.application.playbooks.
//
// The console reads the operator's real playbooks from the API. These are what it shows
// when that read comes back empty, which happens on an unseeded property: an editor with
// nothing in it teaches nobody what a playbook is.
//
// All three pilot playbooks are here, not just the first. The console previously held
// only `pre-arrival-room-readiness` and had no way to open another, so the two playbooks
// the Guest journey cites in its reasoning lines, in-stay-recovery-flag@v1 and
// in-stay-fb-upsell@v1, were named on one screen and unreachable from every other.
//
// The ids are nil-UUID placeholders. A fallback is never saved back: `PlaybookPanel`
// disables saving for one, because POSTing a made-up id under a name the property has
// already used is a 409 from `PlaybookNameTaken`, not an edit.
import type { Playbook } from "../domain/types";

const PRE_ARRIVAL_ROOM_READINESS: Playbook = {
  id: "00000000-0000-0000-0000-000000000001",
  name: "pre-arrival-room-readiness",
  version: 1,
  nodes: [
    {
      key: "trg",
      kind: "trigger",
      spec: { signal_kind: "booking.created" },
      next: ["lead-time"],
    },
    {
      key: "lead-time",
      kind: "guardrail",
      spec: {
        predicate: "lead_hours >= 4",
        on_pass: "auto_execute",
        on_fail: "escalate",
      },
      next: ["note", "alert"],
    },
    {
      key: "note",
      kind: "action",
      spec: {
        kind: "pms.note.add",
        template: "Allergy / preference check for {display_name}",
        auto_approve: true,
        reversible: true,
      },
      next: [],
    },
    {
      key: "alert",
      kind: "action",
      spec: {
        kind: "operator.alert",
        template: "Housekeeping: pre-arrival readiness for room {room_code}",
        auto_approve: false,
        reversible: true,
      },
      next: [],
    },
  ],
};

const IN_STAY_FB_UPSELL: Playbook = {
  id: "00000000-0000-0000-0000-000000000002",
  name: "in-stay-fb-upsell",
  version: 1,
  nodes: [
    {
      key: "trg",
      kind: "trigger",
      spec: { signal_kind: "pos.transaction", when: "category == 'fb'" },
      next: ["fatigue"],
    },
    {
      key: "fatigue",
      kind: "guardrail",
      spec: {
        predicate: "suggestions_last_24h < 3",
        on_pass: "queue",
        on_fail: "block",
      },
      next: ["draft"],
    },
    {
      key: "draft",
      kind: "action",
      spec: {
        kind: "messaging.draft",
        template: "Upsell {next_item} (paired)",
        auto_approve: false,
        reversible: true,
      },
      next: [],
    },
  ],
};

const IN_STAY_RECOVERY_FLAG: Playbook = {
  id: "00000000-0000-0000-0000-000000000003",
  name: "in-stay-recovery-flag",
  version: 1,
  nodes: [
    {
      key: "trg-complaint",
      kind: "trigger",
      spec: { signal_kind: "complaint" },
      next: ["severity"],
    },
    {
      key: "trg-review",
      kind: "trigger",
      spec: { signal_kind: "review.posted", when: "sentiment <= -0.4" },
      next: ["severity"],
    },
    {
      key: "severity",
      kind: "guardrail",
      spec: {
        predicate: "severity IN ['warn', 'critical']",
        on_pass: "queue",
        on_fail: "escalate",
      },
      next: ["alert"],
    },
    {
      key: "alert",
      kind: "action",
      spec: {
        kind: "operator.alert",
        template: "Recovery: {summary}",
        // Never auto-approves: you cannot un-ring a duty manager, and the domain
        // refuses to auto-approve anything irreversible.
        auto_approve: false,
        reversible: false,
      },
      next: [],
    },
  ],
};

/** The pilot seeds, in the order storia.application.playbooks.PILOT_PLAYBOOKS lists them. */
export const SAMPLE_PLAYBOOKS: readonly Playbook[] = [
  PRE_ARRIVAL_ROOM_READINESS,
  IN_STAY_FB_UPSELL,
  IN_STAY_RECOVERY_FLAG,
];
